import React, { useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import './Cadastro.css';
import logo from '../assets/logo-leilancer.png';
import { useAuth } from '../context/AuthContext';
import { trackFunnel } from '../services/analytics';

// Cadastro (/register) — duas etapas:
//   1) dados  (nome, e-mail, WhatsApp)
//   2) acesso (senha + termos) -> cria a conta e entra logado
//
// Eram três: no meio havia um código de confirmação enviado por WhatsApp. Ele
// virou o gargalo da campanha — a pessoa chegava lá e parava, e quando o número
// usado para enviar caía (aconteceu por dias seguidos) ninguém concluía.
// Decisão do cliente em 01/09/2026: trocar dado verificado por conversão.
//
// O WhatsApp continua sendo pedido, mas SEM verificação — serve para a equipe
// entrar em contato, não para provar identidade.
function Cadastro() {
  const navigate = useNavigate();
  const API_URL = process.env.REACT_APP_API_URL;
  const { hydrateMe } = useAuth() || {};

  const [step, setStep] = useState(1);
  const [form, setForm] = useState({
    name: '',
    email: '',
    whatsapp: '',
    password: '',
    confirmPassword: '',
    termos: false,
    showPass: false,
  });

  const [erro, setErro] = useState('');
  const [fieldErrors, setFieldErrors] = useState({});
  const [sucesso, setSucesso] = useState('');
  const [loading, setLoading] = useState(false);
  const registrationStartedRef = useRef(false);

  // ——— Helpers ———
  const onlyDigits = (s = '') => String(s).replace(/\D/g, '');

  const formatWhatsAppMask = (v = '') => {
    const d = onlyDigits(v).slice(0, 11);
    if (d.length <= 2) return d;
    if (d.length <= 6) return `(${d.slice(0, 2)}) ${d.slice(2)}`;
    if (d.length <= 10) return `(${d.slice(0, 2)}) ${d.slice(2, 6)}-${d.slice(6)}`;
    return `(${d.slice(0, 2)}) ${d.slice(2, 7)}-${d.slice(7)}`;
  };

  const emailOk = (em) => /\S+@\S+\.\S+/.test(em);
  const senhaForte = (pwd) => !!pwd && pwd.length >= 8 && /[A-Za-z]/.test(pwd) && /\d/.test(pwd);

  const validarEtapa = (s, p) => {
    const fe = {};
    if (s === 1) {
      if (!p.name?.trim() || p.name.trim().length < 2) fe.name = 'Informe seu nome.';
      if (!emailOk(p.email)) fe.email = 'Informe um e-mail válido.';
      if (onlyDigits(p.whatsapp).length < 10) fe.whatsapp = 'Informe um WhatsApp válido com DDD.';
    } else if (s === 2) {
      if (!senhaForte(p.password)) fe.password = 'A senha deve ter ao menos 8 caracteres, com letras e números.';
      if (p.password !== p.confirmPassword) fe.confirmPassword = 'As senhas não coincidem.';
      if (!p.termos) fe.termos = 'Você precisa aceitar os termos.';
    }
    return fe;
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;

    if (name === 'whatsapp') {
      setForm((prev) => ({ ...prev, whatsapp: formatWhatsAppMask(value) }));
      setFieldErrors((p) => ({ ...p, whatsapp: undefined }));
      return;
    }
    setForm((prev) => ({ ...prev, [name]: type === 'checkbox' ? checked : value }));
    setFieldErrors((p) => ({ ...p, [name]: undefined }));
  };

  const toggleShowPass = () => setForm((prev) => ({ ...prev, showPass: !prev.showPass }));

  const voltar = () => {
    setErro('');
    setSucesso('');
    setFieldErrors({});
    setStep(1);
  };

  // ——— Etapa 1: dados. Nada é enviado ainda; só valida e avança. ———
  const avancar = () => {
    const fe = validarEtapa(1, form);
    if (Object.keys(fe).length) {
      setFieldErrors(fe);
      setErro('Revise os campos destacados.');
      return;
    }
    if (!registrationStartedRef.current) {
      registrationStartedRef.current = true;
      trackFunnel('registration_started', { content_name: 'Cadastro Plataforma Leilancer' });
    }
    setErro('');
    setFieldErrors({});
    setStep(2);
  };

  // ——— Etapa 2: senha. Aqui a conta é criada, sem código de confirmação. ———
  const criarConta = async () => {
    const fe = validarEtapa(2, form);
    if (Object.keys(fe).length) {
      setFieldErrors(fe);
      setErro('Revise os campos destacados.');
      return;
    }

    setLoading(true);
    setErro('');
    try {
      const { data } = await axios.post(`${API_URL}/api/auth/cadastro-direto`, {
        nome: form.name.trim(),
        email: form.email.trim(),
        telefone: onlyDigits(form.whatsapp),
        senha: form.password,
      });

      // Duas contagens, propositalmente separadas:
      //  - a interna registra TODO cadastro (é o número real de conversões);
      //  - a da Meta só sai se a pessoa tiver aceitado a categoria de
      //    publicidade, por isso costuma ser menor.
      if (window.leilancerMeta?.metrica) window.leilancerMeta.metrica('cadastro');
      trackFunnel('complete_registration', {
        content_name: 'Cadastro Plataforma Leilancer',
        status: 'completed',
      });

      if (data?.token) sessionStorage.setItem('token', data.token);
      if (data?.user) sessionStorage.setItem('user', JSON.stringify(data.user));
      if (hydrateMe) {
        try { await hydrateMe(); } catch (e) { /* segue para o painel mesmo assim */ }
      }

      setSucesso('Conta criada! Bem-vindo ao seu teste gratuito de 7 dias 🎉');
      setTimeout(() => window.location.assign('/bem-vindo'), 700);
    } catch (err) {
      const msg = err?.response?.data?.message || 'Não consegui criar sua conta. Tente novamente.';
      // O e-mail foi digitado na etapa 1: volta para lá em vez de deixar a
      // pessoa presa numa tela onde o campo com erro nem aparece.
      if (/email/i.test(msg)) {
        setFieldErrors((x) => ({ ...x, email: msg }));
        setStep(1);
      }
      if (/senha/i.test(msg)) setFieldErrors((x) => ({ ...x, password: msg }));
      setErro(msg);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (loading) return;
    if (step === 1) return avancar();
    return criarConta();
  };

  const titulo = step === 1 ? 'Comece seus 7 dias grátis' : 'Crie sua senha';

  const subtitulo = step === 1
    ? 'Crie sua conta e acesse agora a Plataforma Leilancer.'
    : 'Último passo para liberar seu acesso.';

  const rotuloBotao = step === 1
    ? 'Continuar'
    : (loading ? 'Criando conta…' : 'Criar conta e acessar');

  const etapas = [
    { n: 1, rotulo: 'Seus dados' },
    { n: 2, rotulo: 'Acesso' },
  ];

  return (
    <main className="cad-page">
      <section className="cad-shell" aria-labelledby="cad-titulo">
        {/* ---------- Lateral da marca ---------- */}
        <aside className="cad-brand">
          <img className="cad-brand-logo" src={logo} alt="Leilancer" />

          <div className="cad-brand-copy">
            <h1>Seu próximo lance começa com uma análise melhor.</h1>
            <p>Encontre oportunidades, compare cenários e calcule o retorno antes de decidir.</p>
            <ul className="cad-benefits">
              <li><span className="cad-check" aria-hidden="true">✓</span>7 dias de acesso completo</li>
              <li><span className="cad-check" aria-hidden="true">✓</span>Sem cartão de crédito</li>
              <li><span className="cad-check" aria-hidden="true">✓</span>Busca, filtros e simuladores</li>
            </ul>
          </div>

          <div className="cad-login">
            <span>Já possui uma conta?</span>
            <button type="button" onClick={() => navigate('/login')}>Entrar</button>
          </div>
        </aside>

        {/* ---------- Formulário ---------- */}
        <section className="cad-form-panel">
          <h2 id="cad-titulo">{titulo}</h2>
          <p className="cad-lead">{subtitulo}</p>

          <div className="cad-steps" aria-label="Progresso do cadastro">
            {etapas.map((e) => (
              <div
                key={e.n}
                className={`cad-step ${step === e.n ? 'is-ativo' : ''} ${step > e.n ? 'is-feito' : ''}`}
                aria-current={step === e.n ? 'step' : undefined}
              >
                <b>{step > e.n ? '✓' : e.n}</b>
                <span>{e.rotulo}</span>
              </div>
            ))}
          </div>

          <div className="cad-notice">
            <span className="cad-notice-icon" aria-hidden="true">i</span>
            <span>
              A Plataforma Leilancer oferece ferramentas de busca e análise. Os lances são
              realizados diretamente no site do leiloeiro responsável.
            </span>
          </div>

          <form onSubmit={handleSubmit} noValidate>
            {/* ETAPA 1 — dados */}
            {step === 1 && (
              <>
                <div className="cad-field">
                  <label htmlFor="cad-name">Nome completo</label>
                  <input
                    id="cad-name"
                    name="name"
                    type="text"
                    autoComplete="name"
                    placeholder="Como devemos chamar você?"
                    value={form.name}
                    onChange={handleChange}
                    autoFocus
                    aria-invalid={!!fieldErrors.name}
                  />
                  {fieldErrors.name && <small className="cad-erro-campo">{fieldErrors.name}</small>}
                </div>

                <div className="cad-field">
                  <label htmlFor="cad-email">Seu melhor e-mail</label>
                  <input
                    id="cad-email"
                    name="email"
                    type="email"
                    autoComplete="email"
                    inputMode="email"
                    placeholder="voce@email.com"
                    value={form.email}
                    onChange={handleChange}
                    aria-invalid={!!fieldErrors.email}
                  />
                  {fieldErrors.email && <small className="cad-erro-campo">{fieldErrors.email}</small>}
                </div>

                <div className="cad-field">
                  <label htmlFor="cad-phone">WhatsApp com DDD</label>
                  <input
                    id="cad-phone"
                    name="whatsapp"
                    type="tel"
                    autoComplete="tel"
                    inputMode="tel"
                    placeholder="(11) 99999-9999"
                    maxLength={15}
                    value={form.whatsapp}
                    onChange={handleChange}
                    aria-invalid={!!fieldErrors.whatsapp}
                  />
                  {fieldErrors.whatsapp && <small className="cad-erro-campo">{fieldErrors.whatsapp}</small>}
                </div>

                <p className="cad-helper">Usamos seu WhatsApp só para falar com você sobre a conta.</p>
              </>
            )}

            {/* ETAPA 2 — acesso */}
            {step === 2 && (
              <>
                <div className="cad-field cad-pass">
                  <label htmlFor="cad-senha">Senha</label>
                  <input
                    id="cad-senha"
                    name="password"
                    type={form.showPass ? 'text' : 'password'}
                    autoComplete="new-password"
                    placeholder="Ao menos 8 caracteres"
                    value={form.password}
                    onChange={handleChange}
                                        aria-invalid={!!fieldErrors.password}
                  />
                  <button
                    type="button"
                    className="cad-toggle-pass"
                    onClick={toggleShowPass}
                    aria-label={form.showPass ? 'Ocultar senha' : 'Mostrar senha'}
                  >
                    {form.showPass ? '🙈' : '👁️'}
                  </button>
                  {fieldErrors.password && <small className="cad-erro-campo">{fieldErrors.password}</small>}
                </div>

                <div className="cad-field">
                  <label htmlFor="cad-senha2">Confirmar senha</label>
                  <input
                    id="cad-senha2"
                    name="confirmPassword"
                    type="password"
                    autoComplete="new-password"
                    placeholder="Repita a senha"
                    value={form.confirmPassword}
                    onChange={handleChange}
                    aria-invalid={!!fieldErrors.confirmPassword}
                  />
                  {fieldErrors.confirmPassword && (
                    <small className="cad-erro-campo">{fieldErrors.confirmPassword}</small>
                  )}
                </div>

                <label className="cad-checkbox">
                  <input
                    type="checkbox"
                    name="termos"
                    checked={form.termos}
                    onChange={handleChange}
                    aria-invalid={!!fieldErrors.termos}
                  />
                  <span>
                    Estou de acordo com os{' '}
                    <a href="/termos/" target="_blank" rel="noreferrer">Termos de Uso</a> e a{' '}
                    <a href="/politica-de-privacidade/" target="_blank" rel="noreferrer">Política de Privacidade</a>.
                  </span>
                </label>
                {fieldErrors.termos && <small className="cad-erro-campo">{fieldErrors.termos}</small>}
              </>
            )}

            {erro && <p className="cad-msg-erro">{erro}</p>}
            {sucesso && <p className="cad-msg-ok">{sucesso}</p>}

            <div className="cad-acoes">
              {step > 1 && (
                <button type="button" className="cad-secondary" onClick={voltar} disabled={loading}>
                  Voltar
                </button>
              )}
              <button type="submit" className="cad-primary" disabled={loading} aria-busy={loading}>
                {rotuloBotao}
              </button>
            </div>

            <p className="cad-trust">
              7 dias grátis <span>•</span> Sem cartão <span>•</span> Acesso completo
            </p>
          </form>
        </section>
      </section>

      <nav className="cad-legal" aria-label="Links legais">
        <a href="/politica-de-privacidade/">Privacidade</a>
        <a href="/termos/">Termos de uso</a>
        <a href="/contato">Precisa de ajuda?</a>
      </nav>
    </main>
  );
}

export default Cadastro;
